import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Node, useEditor } from '@craftjs/core';
import { Trans, useTranslation } from 'react-i18next';
import {
  CreateFormConfigDto,
  FilesQuery,
  ICreateFormConfigDto,
  IPatchFormConfigDto,
  PatchFormConfigDto,
  ValidationProblemDetails,
} from '../../../services/api/api-client';
import { QueryFactory } from '../../../services/api';
import { logger } from '../../../application/logging/logging';
import { useModal } from '../../../application/hooks/useModal';
import styles from './barStyles.module.scss';
import { DialogModal } from '../../dialogModal/dialogModal.component';
import { useCommonLocalization } from '../../../application/localisation/useCommonLocalization';
import { AppTextInput } from 'uikit/inputs';
import { DropdownOption } from 'uikit/inputs/dropdown/appDropdownInput';
import { AppDropDownWithSuggestionInput } from 'uikit/inputs/dropdown/appDropDownWithSuggestion';
import { UIEditorSaveResetControls } from './saveResetComponent/UIEditorSaveResetControls';
import { StudyNameComponent } from 'src/components/studyName/studyName.component';
import _ from 'lodash';
import { useFormBuildingPreview } from 'src/features/forms/editing/useFormBuildingPreview';
import { ConfirmResult, useConfirmationModal } from 'src/components/dialogModal/useConfirmationModal';
import { showServerErrorToast, showNotificationToast, showErrorToast } from 'src/components/toast/toast-helper';
import { useForm, useWatch } from 'react-hook-form';
import { HookFormTextInput } from 'src/hookFormControls/hookFormTextInput';
import { HookFormCheckboxField } from 'src/hookFormControls/HookFormCheckboxGroupField';
import { ValidationFormRules } from 'src/helpers/validation-helpers';
import { pascalToCamelCase } from 'src/helpers/error-helpers';
import { getNodesWithSameDataKeys, isNotEmptyImageBlock } from '../uiEditor/uiEditor-helper';
import { useQueryClient } from '@tanstack/react-query';
import { getStudyRolesQueryKey } from 'src/services/api/api-client/UserRolesQuery';
import { FormConfigContext } from '../uiEditor/formConfigContext/formConfig.provider';
import { useContextSelector } from 'use-context-selector';
import { useStudy } from 'src/helpers/hooks/useStudy';
import { createForm, patchForm } from 'src/services/api/api-client/FormsClient';
import { ReactComponent as NewComment } from 'assets/img/notification/new_comment_20.svg';
import { downloadFile } from 'src/helpers/file-helper';
import { AppButton } from 'uikit/buttons';
import useLayoutImport from './importLayout/useLayoutImport';

const USE_FILE_ID_AS_NAME = true;

export const TopBar = () => {
  const { currentFormConfig, saveFormOptions, formConfigs, setCurrentFormConfig } = useContextSelector(
    FormConfigContext,
    (x) => x,
  );
  const queryClient = useQueryClient();
  const study = useStudy();
  const dropDownOptions: DropdownOption<number>[] = useMemo(() => {
    return _.orderBy(formConfigs?.data, [(x) => x.type.toLowerCase()]).map((x) => ({
      text: x.type,
      key: x.id,
    }));
  }, [formConfigs]);

  const selectedDropDownOption = useMemo(
    () => dropDownOptions.find((x) => x.key === currentFormConfig?.id),
    [dropDownOptions, currentFormConfig?.id],
  );

  const { importLayout } = useLayoutImport();

  const { t } = useTranslation('dev');
  const commonLocalizer = useCommonLocalization();
  const saveAsModal = useSaveAsModal();
  const deleteModal = useDeleteConfirmationModal();
  const conflictModal = useConfirmationModal();
  const duplicatedDataKeysModal = useConfirmationModal();

  const [isLoading, setIsLoading] = useState(false);
  const { actions, query } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  const handleConfigSelection = useCallback(
    (value: (typeof dropDownOptions)[0] | undefined) => {
      const remoteConfig = formConfigs?.data?.find((x) => x.id === (value?.key ?? null));
      setCurrentFormConfig?.(remoteConfig ?? null);
    },
    [formConfigs?.data, setCurrentFormConfig],
  );

  const isSchemaValid = useCallback(async () => {
    const allNodes = query.getNodes();
    const nodesWithSameDataKey = getNodesWithSameDataKeys(allNodes);
    if (!Object.keys(nodesWithSameDataKey)?.length) return true;

    const result = await duplicatedDataKeysModal.open({
      title: t('StudySettingsPage.FormBuilder.topbar.DuplicatedDataKeysModal.Title'),
      text: (
        <div className={styles.duplicatedDataKeyContainer}>
          <div>{t('StudySettingsPage.FormBuilder.topbar.DuplicatedDataKeysModal.Text')}</div>
          {Object.entries(nodesWithSameDataKey).map(([dataKey, nodes]) => (
            <div key={dataKey} className={styles.dataKeyContainer}>
              <div>
                <span className={styles.label}>
                  {t('StudySettingsPage.FormBuilder.topbar.DuplicatedDataKeysModal.DataKey')}
                </span>
                {dataKey}
              </div>
              {nodes.map((node, i) => (
                <div key={node.id}>
                  <span className={styles.label}>{i + 1}</span>
                  {node.data.props.label ?? node.data.props.dataKey}
                </div>
              ))}
            </div>
          ))}
        </div>
      ),
      cancelButtonText: t('StudySettingsPage.FormBuilder.topbar.DuplicatedDataKeysModal.CancelButton'),
      okButtonText: t('StudySettingsPage.FormBuilder.topbar.DuplicatedDataKeysModal.OkButton'),
    });

    return result === ConfirmResult.Confirm;
  }, [duplicatedDataKeysModal, query, t]);

  const saveNewConfig = useCallback(async () => {
    if (!study?.id) return;

    const currentSchema = JSON.parse(query.serialize());
    await saveAsModal.open({
      type: saveFormOptions?.type,
      canBeSkipped: !!saveFormOptions?.canBeSkipped,
      isMultiInstance: !!saveFormOptions?.isMultiInstance,
      isPatientCreationForm: !!saveFormOptions?.isPatientCreationForm,
      allowedForNotStartedPatients: !!saveFormOptions?.allowedForNotStartedPatients,

      handler: async (data) => {
        const dto: ICreateFormConfigDto = {
          layoutSchema: currentSchema,
          studyId: study?.id,
          type: data.type,
          isPatientCreationForm: data.isPatientCreationForm,
          isMultiInstance: data.isMultiInstance,
          canBeSkipped: data.canBeSkipped,
          allowedForNotStartedPatients: data.allowedForNotStartedPatients,
        };

        const formDto = await createForm(new CreateFormConfigDto(dto));
        await queryClient.invalidateQueries(getStudyRolesQueryKey());

        actions.history.clear();

        const fetchedConfigsQuery = await formConfigs?.refetch();
        const fetchedForm = fetchedConfigsQuery?.data?.find((x) => x.id === formDto.id);
        setCurrentFormConfig?.(fetchedForm ?? null);
      },
    });
  }, [actions.history, formConfigs, query, queryClient, saveAsModal, saveFormOptions, setCurrentFormConfig, study?.id]);

  const patchConfig = useCallback(async () => {
    if (!currentFormConfig?.id) {
      await saveNewConfig();
      return;
    }

    const currentSchema = JSON.parse(query.serialize());
    const dto: IPatchFormConfigDto = {
      layoutSchema: currentSchema,
      type: saveFormOptions.type,
      isMultiInstance: saveFormOptions.isMultiInstance,
      isPatientCreationForm: saveFormOptions.isPatientCreationForm,
      canBeSkipped: saveFormOptions.canBeSkipped,
      allowedForNotStartedPatients: saveFormOptions.allowedForNotStartedPatients,
    };

    try {
      setIsLoading(true);
      const formDto = await patchForm(currentFormConfig.id, new PatchFormConfigDto(dto));
      actions.history.clear();

      const newVar = await formConfigs?.refetch();
      const fetchedForm = newVar?.data?.find((x) => x.id === formDto.id);
      setCurrentFormConfig?.(fetchedForm ?? null);

      showNotificationToast(
        t('StudySettingsPage.FormBuilder.topbar.FormSavedToast.Message'),
        t('StudySettingsPage.FormBuilder.topbar.FormSavedToast.Header'),
        NewComment,
        3000,
      );
    } catch (error: any) {
      if (error.status === 409) {
        const result = await conflictModal.open({
          title: t('StudySettingsPage.FormBuilder.topbar.ConflictModal.Header'),
          text: t('StudySettingsPage.FormBuilder.topbar.ConflictModal.MainText'),
          cancelButtonText: commonLocalizer('Common_no'),
          okButtonText: commonLocalizer('Common_yes'),
        });

        if (result === ConfirmResult.Confirm) {
          await saveNewConfig();
        }
      } else {
        logger().error(error);
        showServerErrorToast(error);
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    actions.history,
    commonLocalizer,
    conflictModal,
    currentFormConfig?.id,
    formConfigs,
    query,
    saveFormOptions,
    saveNewConfig,
    setCurrentFormConfig,
    t,
  ]);

  const deleteConfig = useCallback(async () => {
    if (!currentFormConfig) return;

    const result = await deleteModal.open({ expectedString: currentFormConfig.type });
    if (result !== ConfirmResult.Confirm) return;

    setIsLoading(true);
    try {
      await QueryFactory.FormsQuery.Client.removeForm(currentFormConfig.id);
      setCurrentFormConfig?.(null);
      formConfigs?.refetch();

      showNotificationToast(
        t('StudySettingsPage.FormBuilder.topbar.FormDeletedToast.Message'),
        t('StudySettingsPage.FormBuilder.topbar.FormDeletedToast.Header'),
        NewComment,
        3000,
      );
    } catch (error: any) {
      logger().error(error);
      showServerErrorToast(error);
    } finally {
      setIsLoading(false);
    }
  }, [currentFormConfig, deleteModal, formConfigs, setCurrentFormConfig, t]);

  const formPreview = useFormBuildingPreview();
  const onPreviewOpen = useCallback(() => {
    formPreview.startFormPreview(JSON.parse(query.serialize()), currentFormConfig?.type || 'No title');
  }, [formPreview, currentFormConfig?.type, query]);

  const downloadImagesHandler = useCallback(async () => {
    const fileIds = getFileIdsFromLayout(query.getNodes());
    if (!fileIds.length) return;

    const images = await FilesQuery.Client.getFiles(fileIds, USE_FILE_ID_AS_NAME);
    downloadFile(images);
  }, [query]);

  const importLayoutHandler = useCallback(async () => {
    try {
      await importLayout({
        onBeforeImport: () => handleConfigSelection(undefined),
      });
      actions.history.clear();
    } catch (error: any) {
      logger().error(error);
      showErrorToast(error.message, t('StudySettingsPage.FormBuilder.topbar.ImportErrorToast'), 5000);
    }
  }, [actions.history, handleConfigSelection, importLayout, t]);

  return (
    <>
      <div className={styles.topToolBox}>
        <div className={styles.newRecord}>
          <StudyNameComponent />

          <AppDropDownWithSuggestionInput
            className={styles.templateDropdown}
            placeholder={t('StudySettingsPage.FormBuilder.topbar.chooseTemplate')}
            options={dropDownOptions}
            value={selectedDropDownOption}
            onChange={handleConfigSelection}
            suggestion={true}
          />
          <AppButton
            colorSchema={'primary'}
            variant={'icon-link'}
            text={t('StudySettingsPage.FormBuilder.topbar.Import')}
            onClick={importLayoutHandler}
          />
        </div>

        <UIEditorSaveResetControls
          isShowControls={true}
          save={async () => {
            const isValid = await isSchemaValid();
            if (!isValid) return;
            await patchConfig();
          }}
          saveAs={async () => {
            const isValid = await isSchemaValid();
            if (!isValid) return;
            await saveNewConfig();
          }}
          downloadImages={downloadImagesHandler}
          exportFileName={currentFormConfig?.type}
          disabled={isLoading}
          canDelete={!!currentFormConfig}
          delete={deleteConfig}
          onPreviewOpen={onPreviewOpen}
        />
      </div>

      {saveAsModal.modal}
      {conflictModal.modal}
      {deleteModal.modal}
      {duplicatedDataKeysModal.modal}
      {formPreview.element}
    </>
  );
};

type OpenDeleteConfirmationModalOptions = {
  expectedString: string;
};

// TODO: Move it to the UIKit component as "Confirmation modal with expected string".
// We actually use it a lot.
const useDeleteConfirmationModal = () => {
  const { t } = useTranslation('dev');
  const commonLocalizer = useCommonLocalization();
  const modal = useModal('CLOSED');
  const promiseRef = useRef<(test: ConfirmResult) => void>();

  const [confirmationInput, setConfirmationInput] = useState<string>();
  const [options, setOptions] = useState<Required<OpenDeleteConfirmationModalOptions>>();

  const closeHandler = useCallback(() => {
    promiseRef?.current?.(ConfirmResult.Closed);
    modal.closeModal();
  }, [modal]);

  return {
    open: (o: OpenDeleteConfirmationModalOptions): Promise<ConfirmResult> => {
      setOptions((cur) => ({ ...cur, ...o }));
      modal.openModal();

      return new Promise<ConfirmResult>((resolve) => {
        promiseRef.current = resolve;
      });
    },
    modal: (
      <DialogModal
        title={t('StudySettingsPage.FormBuilder.topbar.ConfirmModal.Title')}
        visible={modal.visible}
        onHide={closeHandler}
        isClickOutside={true}
        onDismissed={() => setConfirmationInput(undefined)}
        bodyClassName={styles.modalBody}
        footer={{
          leftButton: {
            text: commonLocalizer('Common_Cancel'),
            onClick: () => {
              promiseRef?.current?.(ConfirmResult.Cancel);
              modal.closeModal();
            },
          },
          rightButton: {
            colorSchema: 'destroy',
            text: commonLocalizer('Common_Delete'),
            disabled: confirmationInput !== options?.expectedString,
            onClick: () => {
              promiseRef?.current?.(ConfirmResult.Confirm);
              modal.closeModal();
            },
          },
        }}
      >
        <div className={styles.confirmationMessage}>
          <Trans
            ns={'dev'}
            i18nKey={'StudySettingsPage.FormBuilder.topbar.ConfirmModal.Message'}
            values={{ confirmation: options?.expectedString }}
          >
            <strong className={styles.awfulText} />
            <strong />
          </Trans>
        </div>
        <AppTextInput
          value={confirmationInput ?? ''}
          onChange={(e) => setConfirmationInput(e.target.value)}
          placeholder={t('StudySettingsPage.FormBuilder.topbar.ConfirmModal.Placeholder')}
        />
      </DialogModal>
    ),
  };
};

type FormState = {
  type: string;
  isPatientCreationForm: boolean;
  isMultiInstance: boolean;
  canBeSkipped: boolean;
  allowedForNotStartedPatients: boolean;
};

type OpenSaveAsModalOptions = {
  type: string | undefined;
  isMultiInstance: boolean;
  isPatientCreationForm: boolean;
  allowedForNotStartedPatients: boolean;
  canBeSkipped: boolean;
  handler: (data: FormState) => Promise<void>;
};

const useSaveAsModal = () => {
  const { t } = useTranslation('dev');
  const commonLocalizer = useCommonLocalization();
  const modal = useModal('CLOSED');
  const promiseRef = useRef<(result: ConfirmResult) => void>();
  const handlerRef = useRef<(data: FormState) => Promise<void>>();

  const [generalError, setGeneralError] = useState<string>();

  const form = useForm<FormState>({
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const isMultiInstance = useWatch({ control: form.control, name: 'isMultiInstance' });
  useEffect(() => {
    if (!isMultiInstance) {
      form.setValue('allowedForNotStartedPatients', false);
    }
  }, [form, isMultiInstance]);

  const closeHandler = useCallback(() => {
    promiseRef?.current?.(ConfirmResult.Closed);
    modal.closeModal();
  }, [modal]);

  const submit = form.handleSubmit(async (data) => {
    try {
      await handlerRef.current?.(data);
      promiseRef?.current?.(ConfirmResult.Confirm);
      modal.closeModal();
    } catch (ex: any) {
      if (ex.errors && Object.keys(ex.errors).length > 0) {
        for (const errorKey of Object.keys(ex.errors)) {
          const formKey = pascalToCamelCase(errorKey) as keyof FormState;
          form.setError(formKey, {
            type: 'server',
            message: ex.errors[errorKey][0],
          });
        }
      } else if (ex instanceof ValidationProblemDetails) {
        setGeneralError(ex.detail ?? undefined);
        return;
      } else {
        logger().error(ex);
        const commonError = t('StudySettingsPage.FormBuilder.topbar.errors.cantSave', { detail: ex.detail });
        setGeneralError(commonError);
      }
    }
  });

  return {
    open: (o: OpenSaveAsModalOptions): Promise<ConfirmResult> => {
      form.reset({
        canBeSkipped: o.canBeSkipped,
        isMultiInstance: o.isMultiInstance,
        isPatientCreationForm: o.isPatientCreationForm,
        allowedForNotStartedPatients: o.allowedForNotStartedPatients,
        type: o.type,
      });

      handlerRef.current = o.handler;
      modal.openModal();

      return new Promise<ConfirmResult>((resolve) => {
        promiseRef.current = resolve;
      });
    },
    modal: (
      <DialogModal
        title={t('StudySettingsPage.FormBuilder.topbar.NewFormModal.Title')}
        visible={modal.visible}
        onHide={modal.closeModal}
        bodyClassName={styles.modalBody}
        onDismissed={closeHandler}
        isClickOutside={true}
        footer={{
          errors: generalError,
          leftButton: {
            text: commonLocalizer('Common_Cancel'),
            disabled: form.formState.isSubmitting,
            onClick: () => {
              promiseRef?.current?.(ConfirmResult.Cancel);
              modal.closeModal();
            },
          },
          rightButton: {
            text: commonLocalizer('Common_Save'),
            isLoading: form.formState.isSubmitting,
            disabled: form.formState.isSubmitting,
            onClick: submit,
          },
        }}
      >
        <div className={styles.saveAsModalBody}>
          <HookFormTextInput
            control={form.control}
            name={'type'}
            labelProps={{ text: t('StudySettingsPage.FormBuilder.topbar.NewFormModal.Label') }}
            placeholder={t('StudySettingsPage.FormBuilder.topbar.NewFormModal.Placeholder')}
            rules={{ ...ValidationFormRules().requiredRule }}
          />
          <HookFormCheckboxField
            control={form.control}
            name={'isMultiInstance'}
            label={t('StudySettingsPage.FormBuilder.topbar.multiInstance')}
          />
          <HookFormCheckboxField
            control={form.control}
            name={'allowedForNotStartedPatients'}
            label={t('StudySettingsPage.FormBuilder.topbar.allowedForNotStartedPatients')}
            disabled={!isMultiInstance}
          />
          <HookFormCheckboxField
            control={form.control}
            name={'isPatientCreationForm'}
            label={t('StudySettingsPage.FormBuilder.topbar.patientCreation')}
          />
          <HookFormCheckboxField
            control={form.control}
            name={'canBeSkipped'}
            label={t('StudySettingsPage.FormBuilder.topbar.canBeSkipped')}
          />
        </div>
      </DialogModal>
    ),
  };
};

const getFileIdsFromLayout = (nodes: Record<string, Node>) =>
  Object.values(nodes)
    .filter((x) => isNotEmptyImageBlock(x.data.props))
    .map((x) => x.data.props.fileId);
